import * as _ from 'underscore';

import { updateBreadcrumbs } from '../../app/actions/UpdateBreadcrumbsAction';
import { appStore } from '../../app/AppStore';
import { BreadcrumbStateInfo } from '../../app/content/breadcrumbs/BreadcrumbStateInfo';
import { EditableUserProfilePicture } from '../../app/content/EditableUserProfilePicture';
import { IMainScope } from '../../app/IMainScope';
import { IPlaceScope } from '../../app/IPlaceScope';
import { Messages } from '../../codegen/Localize';
import { imgUrl } from '../../common/utilities/ImgUrl';
import { ResultHelper } from '../helpers/ResultHelper';
import {
  ISubplaceCountsScope,
  ISubplaceTabsScope,
  SubplaceCountsHelper,
  SubplaceTabsHelper,
} from '../helpers/SubplaceCountsHelper';
import { IPlace } from '../models/IPlace';
import { IUserInfo } from '../models/IUserInfo';
import { IUserSettings } from '../models/IUserSettings';
import { Module } from '../Module';
import { IGetUserSettingsResult } from '../ServerApi/requests/GetUserSettingsRequest';
import { IServerUser } from '../ServerApi/types/IServerUser';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { getContentRootStateName } from '../services/ContentUrl';
import { ProfilePictureService } from '../services/ProfilePictureService';
import { ServerService } from '../services/ServerService';
import { IToastOptions, ToasterService, ToastType } from '../services/ToasterService';
import { WindowLocationService } from '../services/WindowLocationService';
import { getSiteUsersPlaceBreadcrumb } from './SiteUsersPlaceCtrl';

export interface ISiteUserPlaceScope extends IMainScope, IPlaceScope,
  ISubplaceCountsScope, ISubplaceTabsScope {
  isSessionSiteOrServerAdmin: boolean;
  isSessionServerAdmin: boolean;
  isSessionUserSelf: boolean;
  isDataAlertsEnabled: boolean;
  user: IUserInfo;
  userSettings: IUserSettings;
  hasUserSettings: boolean;
  siteRole: string;
  userImageUrl: string;
  profilePictureProps: EditableUserProfilePicture.Props;
  flowsOnSiteEnabled: boolean;
}

export class SiteUserPlaceCtrl {

  public static $inject = [
    '$scope',
    '$state',
    '$translate',
    'ServerService',
    'WindowLocationService',
    'ToasterService',
    'ProfilePictureService',

    // resolved in ui router
    'isSessionServerAdmin',
    'isSessionSiteAdmin',
    'isSessionUserSelf',
    'place',
    'siteUser',
    'userSettings',
    // injected by ui router when user is a server admin and not a site member
    'serverUser'
  ];

  constructor(
    $scope: ISiteUserPlaceScope,
    $state: ng.ui.IStateService,
    $translate: ng.translate.ITranslateService,
    ServerService: ServerService,
    WindowLocationService: WindowLocationService,
    toaster: ToasterService,
    profilePictureService: ProfilePictureService,

    // resolved in ui router
    isSessionServerAdmin: boolean,
    isSessionSiteAdmin: boolean,
    isSessionUserSelf: boolean,
    place: IPlace,
    siteUser: ISiteUser,
    userSettingsResult: IGetUserSettingsResult,
    serverUser: IServerUser
  ) {
    $scope.place = place;
    $scope.isDataAlertsEnabled = ServerService.isDataAlertsEnabled();
    $scope.flowsOnSiteEnabled = ServerService.flowsOnSiteEnabled();

    if (siteUser) {
      $scope.siteRole = siteUser.siteRole;
      $scope.user = siteUser;
      $scope.objectName = siteUser.displayName;

      if (siteUser.userImageUrl) {
        $scope.userImageUrl = siteUser.userImageUrl;
      }
    } else if (isSessionServerAdmin) {
      $scope.siteRole = SiteRoles.ServerAdministrator;
      $scope.user = _.omit(serverUser, 'lastSignIn');
      $scope.objectName = serverUser.displayName;

      if (serverUser.userImageUrl) {
        $scope.userImageUrl = serverUser.userImageUrl;
      }
    } else {
      // UI Router should redirect to 'pageDoesNotExist' before we get here.
      throw new Error('User does not exist on this site');
    }

    $scope.profilePictureProps = {
      profileDisplayName: $scope.user.displayName,
      imageUrl: $scope.userImageUrl,
      allowEdit: isSessionUserSelf,
      imageValidator: (imageFile: File, handleValidationResult: (isValidImage: boolean) => void) => {
        const errorMessage: string = profilePictureService.validateImage(imageFile);
        if (errorMessage) {
          showToasterWarningOrReload ({
            type: ToastType.Error,
            text: errorMessage,
          });
        }
        handleValidationResult(!errorMessage);
      },
      handleSubmit: (newUserImage: Blob) => {
        profilePictureService.handlePictureSubmit($scope.user.domainName, $scope.user.username,
          newUserImage, (toasterConfig) => showToasterWarningOrReload(toasterConfig));
      }
    };

    // use the default user image url
    if ($scope.userImageUrl) {
      $scope.userImageUrl = 'url(' + $scope.userImageUrl + ')';
    } else {
      $scope.userImageUrl = imgUrl('user_content_type.svg');
    }

    $scope.userSettings = userSettingsResult;
    $scope.hasUserSettings = !ResultHelper.hasErrors(userSettingsResult);

    $scope.isSessionServerAdmin = isSessionServerAdmin;
    $scope.isSessionSiteOrServerAdmin = isSessionServerAdmin || isSessionSiteAdmin;
    $scope.isSessionUserSelf = isSessionUserSelf;

    if ($scope.isSessionSiteOrServerAdmin) {
        const breadcrumbs: BreadcrumbStateInfo[] = [
          getSiteUsersPlaceBreadcrumb($state, ServerService.isSessionMultiSite()),
          {
            id: '',
            text: $scope.user.displayName
          }
        ];

        appStore.dispatch(updateBreadcrumbs(breadcrumbs));
    } else {
      const breadcrumbs: BreadcrumbStateInfo[] = [
        {
          text: Messages.backToContent(),
          href: $state.href(getContentRootStateName()),
          testId: 'back-to-content-link'
        }
      ];

      appStore.dispatch(updateBreadcrumbs(breadcrumbs));
    }

    SubplaceCountsHelper.keepSubplaceCountsUpdated($scope, place);
    SubplaceTabsHelper.refreshWhenCurrentTabClicked($scope, $state);

    function showToasterWarningOrReload(toasterConfig: IToastOptions) {
      if (toasterConfig) {
        toaster.pop(toasterConfig);
      } else {
        WindowLocationService.reload();
      }
    }

  }

}

Module.instance().controller('SiteUserPlaceCtrl', SiteUserPlaceCtrl);
